import React, { useState, useEffect, createContext, useContext, ReactNode } from 'react';
import { Auth, Hub } from 'aws-amplify';
import { authService, configureAmplify } from '../services/auth';

interface User {
  id: string;
  email: string;
  name?: string;
  attributes?: Record<string, any>;
}

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  signIn: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  signUp: (email: string, password: string, name?: string) => Promise<{ success: boolean; error?: string }>;
  signOut: () => Promise<void>;
  confirmSignUp: (email: string, code: string) => Promise<{ success: boolean; error?: string }>;
  resendConfirmationCode: (email: string) => Promise<{ success: boolean; error?: string }>;
  forgotPassword: (email: string) => Promise<{ success: boolean; error?: string }>;
  forgotPasswordSubmit: (email: string, code: string, newPassword: string) => Promise<{ success: boolean; error?: string }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Ensure Amplify is configured
    configureAmplify();

    // Handle OAuth callback if present
    const handleOAuthCallback = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const code = urlParams.get('code');
      
      if (code) {
        try {
          // Let Amplify handle the OAuth callback
          await Auth.currentAuthenticatedUser();
          // Clean up the URL
          window.history.replaceState({}, document.title, window.location.pathname);
        } catch (error) {
          console.error('Error processing OAuth callback:', error);
        }
      }
    };

    handleOAuthCallback().then(() => {
      checkAuthState();
    });
    
    // Listen for authentication events
    const unsubscribe = Hub.listen('auth', ({ payload: { event, data } }) => {
      switch (event) {
        case 'signIn':
          console.log('User signed in:', data);
          setUser(mapCognitoUser(data));
          setIsLoading(false);
          break;
        case 'signUp':
          console.log('User signed up:', data);
          break;
        case 'signOut':
          console.log('User signed out');
          setUser(null);
          setIsLoading(false);
          break;
        case 'signIn_failure':
          console.log('User sign in failed:', data);
          setIsLoading(false);
          break;
        case 'tokenRefresh':
          console.log('Token refresh succeeded');
          break;
        case 'tokenRefresh_failure':
          console.log('Token refresh failed');
          break;
        case 'configured':
          console.log('Auth configured');
          break;
        case 'cognitoHostedUI':
          console.log('Cognito Hosted UI event:', data);
          checkAuthState();
          break;
        case 'cognitoHostedUI_failure':
          console.log('Cognito Hosted UI failure:', data);
          setIsLoading(false);
          break;
        default:
          break;
      }
    });

    return () => unsubscribe();
  }, []);

  const checkAuthState = async () => {
    try {
      console.log('Checking auth state...');
      const cognitoUser = await Auth.currentAuthenticatedUser();
      console.log('Current authenticated user:', cognitoUser);
      setUser(mapCognitoUser(cognitoUser));
    } catch (error) {
      console.log('No authenticated user found:', error);
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  };

  const mapCognitoUser = (cognitoUser: any): User => {
    return {
      id: cognitoUser.attributes?.sub || cognitoUser.username,
      email: cognitoUser.attributes?.email || cognitoUser.username,
      name: cognitoUser.attributes?.name,
      attributes: cognitoUser.attributes,
    };
  };

  const signIn = async (email: string, password: string) => {
    const result = await authService.signIn(email, password);
    if (result.success && result.user) {
      setUser(mapCognitoUser(result.user));
    }
    return result;
  };

  const signUp = async (email: string, password: string, name?: string) => {
    return await authService.signUp(email, password, name);
  };

  const signOut = async () => {
    await authService.signOut();
    setUser(null);
  };

  const confirmSignUp = async (email: string, code: string) => {
    return await authService.confirmSignUp(email, code);
  };

  const resendConfirmationCode = async (email: string) => {
    return await authService.resendConfirmationCode(email);
  };

  const forgotPassword = async (email: string) => {
    return await authService.forgotPassword(email);
  };

  const forgotPasswordSubmit = async (email: string, code: string, newPassword: string) => {
    return await authService.forgotPasswordSubmit(email, code, newPassword);
  };

  const value = {
    user,
    isLoading,
    isAuthenticated: !!user,
    signIn,
    signUp,
    signOut,
    confirmSignUp,
    resendConfirmationCode,
    forgotPassword,
    forgotPasswordSubmit,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};